import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../contexts/AuthContext";
import { useTeam } from "../contexts/TeamContext";
import { api } from "../lib/chalkApi";
import { startCheckout } from "../lib/checkout";
import {
  BottomSheet,
  ChalkCard, ChalkIllustrationLines, EmptyState, RsvpRow, SectionTitle,
  SportBadge, WhiteHeader,
} from "../components/chalk/primitives";
import EventWeatherBadge from "../components/chalk/EventWeatherBadge";

function humanWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const tomorrow = new Date(); tomorrow.setDate(now.getDate() + 1);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (sameDay(d, now)) return `Today · ${time}`;
  if (sameDay(d, tomorrow)) return `Tomorrow · ${time}`;
  return d.toLocaleDateString([], { month: "short", day: "numeric" }) + ` · ${time}`;
}

export default function Clipboard() {
  const { user } = useAuth();
  const { teams, tier } = useTeam();
  const navigate = useNavigate();
  const [sectionData, setSectionData] = useState({ today: [], coming: [], messages: [] });
  const [conflicts, setConflicts] = useState([]);
  const [activeConflict, setActiveConflict] = useState(null);
  const [banners, setBanners] = useState([]);
  const [myRsvps, setMyRsvps] = useState({}); // { eventId: { athleteId: status } }
  const [athletesByTeam, setAthletesByTeam] = useState({}); // { teamId: [{id, first}] }

  useEffect(() => {
    let cancelled = false;
    async function loadBanners() {
      try {
        const data = await api.banners();
        if (!cancelled) setBanners(data.banners || []);
      } catch { /* ignore */ }
    }
    loadBanners();
    const t = setInterval(loadBanners, 8000);
    // Post-signup funnel — kick parents straight into Clipboard+ checkout if
    // they picked "I'm a parent" in step 0 of signup.
    try {
      const want = sessionStorage.getItem("chalk_wants_checkout");
      if (want === "clipboard_plus" && user?.role === "guardian") {
        sessionStorage.removeItem("chalk_wants_checkout");
        api.mySubscription().then((d) => {
          const s = d?.subscription;
          if (s && (s.status === "active" || s.status === "trialing")) return;
          startCheckout({ tier: "clipboard_plus" }).catch(() => {});
        }).catch(() => {});
      }
    } catch { /* ignore */ }
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  async function dismissBanner(id) {
    setBanners((arr) => arr.filter((b) => b.id !== id));
    try { await api.dismissBanner(id); } catch { /* ignore */ }
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const today = [];
      const coming = [];
      const allMessages = [];
      const athletes = {};
      const rsvpMap = {};

      for (const t of teams) {
        try {
          const [ev, msgs, ros] = await Promise.all([
            api.listEvents(t.id), api.listMessages(t.id), api.listRoster(t.id),
          ]);
          const myAthletes = (ros.athletes || []).filter((a) =>
            (a.guardians || []).some((g) => g.user_id === user.id)
          );
          athletes[t.id] = myAthletes;
          for (const e of ev.events || []) {
            if (e.status === "cancelled") continue;
            const dt = new Date(e.local_time);
            const now = new Date();
            const diffDays = Math.floor((dt - now) / (1000 * 60 * 60 * 24));
            const rec = { ...e, team_name: t.name, team_sport: t.sport, my_athletes: myAthletes };
            if (dt.toDateString() === now.toDateString()) today.push(rec);
            else if (diffDays >= 0 && diffDays < 14) coming.push(rec);
            // Best-effort pre-fetch of my RSVPs for this event
            try {
              const mine = await api.myRsvpsForEvent(e.id);
              const m = {};
              for (const row of (mine.mine || [])) m[row.athlete_id] = row.status;
              if (Object.keys(m).length) rsvpMap[e.id] = m;
            } catch {/* ignore */}
          }
          for (const m of msgs.messages || []) allMessages.push({ ...m, team_name: t.name });
        } catch {/* per-team error */}
      }
      today.sort((a, b) => new Date(a.local_time) - new Date(b.local_time));
      coming.sort((a, b) => new Date(a.local_time) - new Date(b.local_time));
      allMessages.sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at));
      if (cancelled) return;
      setAthletesByTeam(athletes);
      setMyRsvps(rsvpMap);
      setSectionData({ today, coming: coming.slice(0, 10), messages: allMessages.slice(0, 6) });
      if (tier.guardian_tier === "clipboard_plus") {
        setConflicts(detectConflicts([...today, ...coming]));
      }
    }
    load();
    return () => { cancelled = true; };
  }, [teams, tier.guardian_tier, user.id]);

  async function rsvp(event, athleteId, status) {
    const prevStatus = myRsvps[event.id]?.[athleteId];
    // Optimistic update
    setMyRsvps((m) => ({ ...m, [event.id]: { ...(m[event.id] || {}), [athleteId]: status } }));

    // 5-second undo toast
    const undoTimer = { reverted: false };
    toast(
      status === "going" ? "You're in." : status === "late" ? "Got it — running late." : "Won't make it.",
      {
        duration: 5000,
        action: {
          label: "Undo",
          onClick: () => {
            undoTimer.reverted = true;
            setMyRsvps((m) => ({
              ...m,
              [event.id]: { ...(m[event.id] || {}), [athleteId]: prevStatus },
            }));
            if (prevStatus) {
              api.setRsvp(event.id, athleteId, prevStatus).catch(() => {});
            }
          },
        },
        cancel: { label: "Dismiss" },
      }
    );

    try {
      await api.setRsvp(event.id, athleteId, status);
    } catch (e) {
      if (!undoTimer.reverted) {
        toast.error(e.message || "Couldn't save that.");
        setMyRsvps((m) => ({
          ...m,
          [event.id]: { ...(m[event.id] || {}), [athleteId]: prevStatus },
        }));
      }
    }
  }

  const hasAnyContent = sectionData.today.length || sectionData.coming.length || sectionData.messages.length;

  return (
    <>
      <WhiteHeader
        title={`Hi, ${user.first_name}.`}
        subtitle={teams.length ? `${teams.length} team${teams.length > 1 ? "s" : ""} in the mix.` : "No teams yet."}
        avatar={<div style={{ width: 36, height: 36, borderRadius: 999, background: "var(--chalk-green)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 500, fontSize: 14 }}>{(user.first_name || "?")[0]}</div>}
      />
      <div style={{ padding: "0 18px 24px" }}>
        {banners.length > 0 && (
          <div style={{ marginTop: 14, display: "grid", gap: 8 }} data-testid="emergency-banners">
            {banners.map((b) => {
              const isCancel = b.trigger === "event_cancelled";
              const bg = isCancel ? "var(--chalk-red-bg)" : "var(--chalk-amber-bg)";
              const fg = isCancel ? "var(--chalk-red)" : "var(--chalk-amber)";
              const border = isCancel ? "#F1C5C5" : "#E9D2A6";
              const aria = isCancel ? "assertive" : "polite";
              return (
                <div
                  key={b.id}
                  data-testid={`banner-${b.trigger}`}
                  role="alert"
                  aria-live={aria}
                  style={{
                    background: bg, color: fg, border: `0.5px solid ${border}`,
                    borderRadius: 12, padding: 14, display: "flex", gap: 10, alignItems: "flex-start",
                  }}
                >
                  <AlertTriangle size={18} aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>
                      {isCancel ? "Heads up — a play was cancelled." : "Heads up — a play is delayed."}
                    </div>
                    <div style={{ fontSize: 13, marginTop: 4, color: "var(--chalk-board)" }}>
                      {b.body}
                    </div>
                  </div>
                  <button
                    aria-label="Dismiss"
                    data-testid={`banner-dismiss-${b.id}`}
                    onClick={() => dismissBanner(b.id)}
                    style={{ background: "transparent", border: "none", color: fg, cursor: "pointer", padding: 0 }}
                  >
                    <X size={16} aria-hidden="true" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {conflicts.length > 0 && (
          <div
            className="chalk-card"
            data-testid="conflict-banner"
            onClick={() => setActiveConflict(conflicts[0])}
            style={{ marginTop: 14, borderLeft: "2px solid var(--chalk-amber)", cursor: "pointer" }}
          >
            <div style={{ fontSize: 14, fontWeight: 500, color: "var(--chalk-amber)" }}>
              Heads up — {conflicts.length === 1 ? "two events overlap." : `${conflicts.length} schedule conflicts.`}
            </div>
            <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginTop: 4 }}>
              {conflicts[0].summary} · Tap to compare
            </div>
          </div>
        )}

        {!hasAnyContent ? (
          <EmptyState
            icon={<ChalkIllustrationLines />}
            headline="Your coach is drawing up the season."
            body="Check back soon — new plays will appear here."
          />
        ) : (
          <>
            {sectionData.today.length > 0 && <SectionTitle label="Today" />}
            {sectionData.today.map((e) => (
              <EventBlock key={e.id} event={e} myRsvps={myRsvps[e.id] || {}} onRsvp={rsvp} tier={tier.guardian_tier} />
            ))}

            {sectionData.coming.length > 0 && <SectionTitle label="Coming up" />}
            {sectionData.coming.map((e) => (
              <EventBlock key={e.id} event={e} myRsvps={myRsvps[e.id] || {}} onRsvp={rsvp} tier={tier.guardian_tier} />
            ))}

            {sectionData.messages.length > 0 && <SectionTitle label="From your coaches" />}
            {sectionData.messages.map((m) => (
              <ChalkCard key={m.id} data-testid={`msg-${m.id}`} onClick={() => navigate("/app/talk")}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <div style={{ fontSize: 14, color: "var(--chalk-board)" }}>{m.body}</div>
                </div>
                <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 6 }}>
                  {m.team_name} · {humanWhen(m.sent_at)}
                </div>
              </ChalkCard>
            ))}
          </>
        )}
      </div>

      <BottomSheet isOpen={!!activeConflict} title="Schedule conflict" onDismiss={() => setActiveConflict(null)}>
        {activeConflict && (
          <div data-testid="conflict-viewer">
            <p style={{ fontSize: 13, color: "var(--chalk-muted)", marginBottom: 12 }}>
              Two events overlap. Pick which family member goes where.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {[activeConflict.a, activeConflict.b].map((e) => (
                <div key={e.id} className="chalk-card" style={{ padding: 12 }}>
                  <SportBadge sport={e.team_sport} />
                  <div style={{ fontSize: 14, fontWeight: 500, color: "var(--chalk-board)", marginTop: 8 }}>{e.title}</div>
                  <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 4 }}>
                    {e.team_name}
                  </div>
                  <div style={{ fontSize: 13, color: "var(--chalk-board)", marginTop: 8 }}>
                    {humanWhen(e.local_time)}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 2 }}>
                    {e.location || "TBD"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </BottomSheet>
    </>
  );
}

function EventBlock({ event, myRsvps, onRsvp, tier }) {
  const myAthletes = event.my_athletes || [];
  return (
    <ChalkCard data-testid={`event-${event.id}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 500, color: "var(--chalk-board)" }}>{event.title}</div>
          <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginTop: 4 }}>
            {humanWhen(event.local_time)} · {event.location || "TBD"} · {event.team_name}
          </div>
          <div style={{ marginTop: 6 }}>
            <EventWeatherBadge eventId={event.id} />
          </div>
        </div>
        <SportBadge sport={event.team_sport} />
      </div>
      {myAthletes.map((a) => (
        <div key={a.id} style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginBottom: 4 }}>
            {a.first_name} {a.last_name}
          </div>
          <RsvpRow
            tier={tier}
            currentStatus={myRsvps[a.id]}
            onRsvp={(status) => onRsvp(event, a.id, status)}
            testIdPrefix={`rsvp-${event.id}-${a.id}`}
          />
        </div>
      ))}
      {myAthletes.length === 0 && (
        <RsvpRow
          tier={tier}
          currentStatus={null}
          onRsvp={() => {}}
          testIdPrefix={`rsvp-${event.id}`}
        />
      )}
    </ChalkCard>
  );
}

function detectConflicts(events) {
  const out = [];
  for (let i = 0; i < events.length; i++) {
    for (let j = i + 1; j < events.length; j++) {
      const a = new Date(events[i].local_time);
      const b = new Date(events[j].local_time);
      const aEnd = events[i].end_local_time ? new Date(events[i].end_local_time) : new Date(a.getTime() + 3600 * 1000);
      const bEnd = events[j].end_local_time ? new Date(events[j].end_local_time) : new Date(b.getTime() + 3600 * 1000);
      const overlap = a < bEnd && b < aEnd;
      const within30 = Math.abs(a - b) / 60000 <= 30;
      if ((overlap || within30) && events[i].team_name !== events[j].team_name) {
        out.push({
          a: events[i],
          b: events[j],
          summary: `${events[i].team_name} (${events[i].title}) vs ${events[j].team_name} (${events[j].title})`,
        });
      }
    }
  }
  return out;
}
